import getAzureSasToken from '../../utils/azureSasToken';

const ACCOUNT_NAME = process.env.REACT_APP_AZURE_STORAGE_ACCOUNT_NAME;
// O SAS Token deve ser a string de consulta completa, começando com '?'
const SAS_TOKEN = getAzureSasToken();
const CONTAINER_NAME = 'colaboradorfotos';

/**
 * Verifica se as variáveis de ambiente do Azure estão configuradas.
 */
const checkAzureConfig = () => {
  if (!ACCOUNT_NAME || !SAS_TOKEN) {
    throw new Error("As variáveis de ambiente para o Azure Blob Storage não estão configuradas. Verifique REACT_APP_AZURE_STORAGE_ACCOUNT_NAME e REACT_APP_CONTAINER_SAS_TOKEN em seu arquivo .env");
  }
};

/**
 * Gera as URLs para upload e visualização final do arquivo.
 * @param {string} idRede - O ID da rede.
 * @param {string} blobName - O nome do blob (ex: 'id-colaborador.jpg').
 * @returns {{sasUrl: string, fileUrl: string}}
 */
const getUrls = (idRede, blobName) => {
  checkAzureConfig();
  const blobPath = `${idRede}/${blobName}`;
  const fileUrl = `https://${ACCOUNT_NAME}.blob.core.windows.net/${CONTAINER_NAME}/${blobPath}`;
  const sasUrl = `${fileUrl}${SAS_TOKEN}`;
  return { sasUrl, fileUrl };
};

/**
 * Envia um arquivo para o Azure Blob Storage usando uma SAS URL.
 * @param {string} sasUrl - A URL pré-assinada para o upload.
 * @param {File} file - O arquivo a ser enviado.
 */
const uploadToBlob = async (sasUrl, file) => {
  const uploadResponse = await fetch(sasUrl, {
    method: 'PUT',
    headers: {
      'x-ms-blob-type': 'BlockBlob',
      'Content-Type': file.type || 'application/octet-stream',
    },
    body: file,
  });

  if (!uploadResponse.ok) {
    const errorText = await uploadResponse.text();
    console.error('Azure Blob Upload Error:', errorText);
    throw new Error(`Falha ao enviar para o Blob Storage. Status: ${uploadResponse.status}`);
  }
};

/**
 * Função principal para fazer o upload da foto de um colaborador.
 * @param {File} file - O objeto do arquivo a ser enviado.
 * @param {string} idRede - O ID da rede.
 * @param {string} blobName - O nome desejado para o blob.
 * @returns {Promise<{url: string}>} A URL final do arquivo enviado.
 */
const uploadFoto = async (file, idRede, blobName) => {
  const { sasUrl, fileUrl } = getUrls(idRede, blobName);
  await uploadToBlob(sasUrl, file);
  return { url: fileUrl };
};

export default uploadFoto;

/**
 * Deleta um blob do Azure Blob Storage.
 * @param {string} blobUrl - A URL completa do blob a ser deletado.
 */
export const deleteFoto = async (blobUrl) => {
  checkAzureConfig();
  const deleteUrl = `${blobUrl}${SAS_TOKEN}`;

  const response = await fetch(deleteUrl, {
    method: 'DELETE',
  });

  if (!response.ok && response.status !== 404) { // 404 está ok, significa que já foi deletado
    const errorText = await response.text();
    console.error('Azure Blob Deletion Error:', errorText);
    throw new Error(`Falha ao deletar o blob. Status: ${response.status}`);
  }
};

/**
 * Anexa o SAS token a uma URL de blob para torná-la visualizável.
 * @param {string} blobUrl - A URL base do blob.
 * @returns {string} A URL completa e visualizável com o SAS token.
 */
export const getViewableUrl = (blobUrl) => {
  if (!blobUrl || blobUrl.includes('?')) {
    return blobUrl || '';
  }
  checkAzureConfig();
  return `${blobUrl}${SAS_TOKEN}`;
};
